import { Box, Center, Flex, Image, Link, Spinner, Text } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { BrowserView, MobileView } from 'react-device-detect';
import { Link as ReactRouterLink } from 'react-router-dom';
import Marble from '../../assets/icons/marble.png';
import OfflineEye from '../../assets/icons/OfflineEye.svg';
import { isAuthenticated } from '../../auth/keycloak';
import { getGameIcon, getS6ColourFromOnlineStatus } from '../../service/games';
import { AvatarImage } from '../profileComponents/AvatarImage';
import { FOLLOWING_COLOR, OFFLINE_COLOR, ONLINE_COLOR } from '../styles';
import { convertToDisplayData, ListItemDisplayData, LiveSixListItemProps } from './LiveSixListItem';
import { LiveSocialLinks } from './LiveSocialLinks';
import { NotificationToggle } from './NotificationToggle';
import { OfflineBadge, PlayerBadge, ViewerBadge } from './ViewerBadge';

export const SixListItemCompact: React.FC<LiveSixListItemProps> = ({
    gameInfo,
    profile,
    displayNotifications = true,
    following,
}) => {
    const [profileToDisplay, setProfileToDisplay] = useState(gameInfo?.userProfile);
    const [displayData, setDisplayData] = useState<ListItemDisplayData>();
    const [isLoading, setIsLoading] = useState(true);
    const [icon, setIcon] = useState<string>(Marble);
    const [followingUser, setFollowingUser] = useState<boolean | undefined>();
    const [borderColour, setBorderColour] = useState('');
    const { keycloak } = useKeycloak();

    useEffect(() => {
        setProfileToDisplay(
            profile ||
                (!!gameInfo?.userProfile
                    ? { ...gameInfo?.userProfile, twitch_stream: undefined }
                    : undefined),
        );
    }, [gameInfo, profile]);

    useEffect(() => {
        const loadData = async () => {
            const displayData = profileToDisplay
                ? await convertToDisplayData(profileToDisplay, gameInfo)
                : undefined;
            setDisplayData(displayData);
            if (displayData) {
                if (displayData.onlineStatus) {
                    // Check if the rendered userID is present in the following list
                    const isFollowing = following?.some(
                        (user) => user.id === displayData.streamSixID,
                    );

                    setFollowingUser(isFollowing);

                    if (isFollowing) {
                        setBorderColour('#3FA5F0');
                    } else {
                        setBorderColour('#6EF1D5');
                    }
                } else {
                    setIcon(OfflineEye);
                    setBorderColour('#FE8FB6');
                }
                setIsLoading(false);
            }
        };

        loadData();
    }, [gameInfo, following, profileToDisplay]);

    return !profileToDisplay ? null : (
        <Flex className={'live-six-compact-container'}>
            {isLoading ? (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            ) : (
                displayData && (
                    <>
                        <BrowserView className="live-six-compact-browser">
                            <Link
                                as={ReactRouterLink}
                                to={`/user/${displayData.streamSixID}`}
                                state={displayData}
                                className={`browser-streamer-pic ${
                                    !displayData.onlineStatus && 'offline'
                                }`}
                                border={`1px solid ${
                                    displayData.onlineStatus ? borderColour : '#FE8FB6'
                                }`}
                            >
                                <Flex className={`compact-stack ${followingUser && 'following'}`}>
                                    <Flex className="compact-avatar-container">
                                        <AvatarImage
                                            src={displayData.avatarImageUrl}
                                            className={'compact-profile-image'}
                                        />
                                    </Flex>
                                </Flex>
                            </Link>
                            <Flex className={`live-six-browser ${followingUser && 'following'}`}>
                                <Flex className={'browser-streamer-info'}>
                                    <Flex className={'browser-streamer-text'} direction={'column'}>
                                        <Flex
                                            className={`streamer-name`}
                                            background={
                                                displayData.onlineStatus
                                                    ? followingUser
                                                        ? FOLLOWING_COLOR
                                                        : ONLINE_COLOR
                                                    : OFFLINE_COLOR
                                            }
                                        >
                                            <Text>{displayData.usernameText}</Text>
                                        </Flex>
                                        {displayData.titleText && (
                                            <Text className={'streamer-game'}>
                                                {displayData.titleText}
                                            </Text>
                                        )}
                                        {displayData.descriptionText && (
                                            <Text className={'streamer-description'}>
                                                {displayData.descriptionText}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                                <Flex
                                    className={`browser-streamer-status ${
                                        !displayData.onlineStatus && 'offline'
                                    }`}
                                >
                                    {/* Play Button */}
                                    {displayData.onlineStatus && gameInfo ? (
                                        <Box className={'browser-play'}>
                                            <Link
                                                as={ReactRouterLink}
                                                to={`/game/${gameInfo.license}`}
                                            >
                                                <Center
                                                    className="play-container"
                                                    border={`2px solid ${
                                                        followingUser ? `#3FA5F0` : `#6EF1D5`
                                                    }`}
                                                >
                                                    <Image
                                                        className={'online-eye'}
                                                        src={getGameIcon(gameInfo.license)}
                                                    />
                                                </Center>
                                            </Link>
                                        </Box>
                                    ) : (
                                        <Flex className={'browser-offline-badge'}>
                                            <OfflineBadge />
                                        </Flex>
                                    )}
                                    {/* Live Status and Count */}
                                    {displayData.onlineStatus && (
                                        <>
                                            <ViewerBadge
                                                count={displayData.viewCount}
                                                width={'45px'}
                                            />
                                            <Flex className={'compact-bottom-row'}>
                                                {displayData.streamingStatus && (
                                                    <Flex
                                                        className={`compact-live-social-links ${
                                                            followingUser && 'following'
                                                        }`}
                                                    >
                                                        <LiveSocialLinks
                                                            profile={profileToDisplay}
                                                        />
                                                    </Flex>
                                                )}
                                                <PlayerBadge
                                                    count={displayData.activePlayerCount}
                                                    width={'58px'}
                                                />
                                            </Flex>
                                        </>
                                    )}
                                </Flex>
                            </Flex>
                        </BrowserView>
                        <MobileView className={'live-six-compact-mobile'}>
                            {/* Play Button */}
                            <Box className={`compact-play`}>
                                {displayData.onlineStatus && gameInfo ? (
                                    <Link as={ReactRouterLink} to={`/game/${gameInfo.license}`}>
                                        <Center
                                            className="play-container"
                                            border={`2px solid ${
                                                followingUser ? `#3FA5F0` : `#6EF1D5`
                                            }`}
                                        >
                                            <Image
                                                className={'online-eye'}
                                                src={getGameIcon(gameInfo.license)}
                                            />
                                        </Center>
                                    </Link>
                                ) : (
                                    <Center className="play-container offline">
                                        <Image src={icon} className={'offline-eye'} />
                                    </Center>
                                )}
                            </Box>
                            <Link
                                flex={1}
                                as={ReactRouterLink}
                                to={`/user/${displayData.streamSixID}`}
                                state={displayData}
                                className={`compact-streamer-info ${
                                    !displayData.onlineStatus && 'offline'
                                }`}
                            >
                                <Flex className={`compact-stack ${followingUser && 'following'}`}>
                                    {/* Avatar Section */}

                                    <Flex className="compact-avatar-container">
                                        <AvatarImage
                                            src={displayData.avatarImageUrl}
                                            className={'compact-profile-image'}
                                        />
                                    </Flex>

                                    {/* Creator Data Text */}
                                    <Flex className={'compact-stack-info'}>
                                        <Flex className="compact-stack-text">
                                            <Text className="compact-username-text">
                                                {displayData.usernameText}
                                            </Text>
                                            <Flex className="compact-text-holder">
                                                <Flex className="compact-flex-container">
                                                    {displayData.titleText && (
                                                        <Text className="name-game-text">
                                                            {displayData.titleText}
                                                        </Text>
                                                    )}
                                                    {displayData.descriptionText && (
                                                        <Text
                                                            className="game-info-text"
                                                            noOfLines={1}
                                                        >
                                                            {displayData.descriptionText}
                                                        </Text>
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                        {/* Live Status and Count */}
                                        {displayData.onlineStatus && (
                                            <Flex className={'online-icons'}>
                                                <Flex className={'live-six-viewer-container'}>
                                                    <ViewerBadge
                                                        count={displayData.viewCount}
                                                        width={'45px'}
                                                    />
                                                    <PlayerBadge
                                                        count={displayData.activePlayerCount}
                                                        width={'58px'}
                                                    />
                                                </Flex>
                                                {displayData.streamingStatus && (
                                                    <Flex
                                                        className={`compact-live-social-links ${
                                                            followingUser && 'following'
                                                        }`}
                                                    >
                                                        <LiveSocialLinks
                                                            profile={profileToDisplay}
                                                        />
                                                    </Flex>
                                                )}
                                            </Flex>
                                        )}
                                    </Flex>
                                </Flex>
                            </Link>
                            {displayNotifications && isAuthenticated(keycloak) && (
                                <Box
                                    className={`compact-notifications ${getS6ColourFromOnlineStatus(
                                        displayData.onlineStatus,
                                    )}-border`}
                                >
                                    <Center className="compact-container">
                                        <NotificationToggle
                                            streamSixIDText={displayData.streamSixID}
                                            keycloak={keycloak}
                                        />
                                    </Center>
                                </Box>
                            )}
                        </MobileView>
                    </>
                )
            )}
        </Flex>
    );
};
